import { Authentication } from '@icabbi/vue-authentication';
import { customer } from '@icabbi/api-gateway-client';

const defaultState = {
  bookingChannelConnector: null,
  setBookingChannelStatesLoading: false,
  authCallbackUrl: '',
  colorScheme: {
    primary: '',
    error: '',
  },
  companyName: undefined,
  id: undefined,
  logoURL: undefined,
  oidcClientId: '',
  postLogoutUrl: '',
  currencySymbol: '',
  fleets: [],
};

const getters = {
  authCallbackUrl: state => state.authCallbackUrl,
  bookingChannelId: state => state.id,
  companyName: state => state.companyName,
  logoURL: state => state.logoURL,
  oidcClientId: state => state.oidcClientId,
  postLogoutUrl: state => state.postLogoutUrl,
  theme: state => state.colorScheme,
  currencySymbol: state => state.currencySymbol,
  fleets: state => state.fleets,
  multiFleetEnabled: state => state.fleets && state.fleets.length > 1,

  // eslint-disable-next-line no-shadow
  bookingChannelConnector: (state, getters, rootState, rootGetters) => ({ dispatch }) => {
    if (state.bookingChannelConnector === null) {
      const authentication = Authentication({ dispatch, getters: rootGetters });
      const { authenticationFailureInterceptor, bearerTokenInterceptor } = authentication;
      const bookingChannelConnector = new customer.BookingChannelConnector({
        baseURL: process.env.VUE_APP_API_GATEWAY_URL,
        timeout: parseInt(process.env.VUE_APP_HTTP_REQUEST_TIMEOUT, 10),
      });
      bookingChannelConnector.httpClient.interceptors.response.use(undefined, error => authenticationFailureInterceptor(error));
      bookingChannelConnector.httpClient.interceptors.request.use(config => bearerTokenInterceptor(config));
      state.bookingChannelConnector = bookingChannelConnector;
    }
    return state.bookingChannelConnector;
  },
};

const actions = {

  // ======== Privacy Policy ========
  async getBookingChannelPrivacyPolicyList(context, { bookingChannelId }) {
    try {
      const bookingChannelConnector = context.getters.bookingChannelConnector(context);
      return await bookingChannelConnector.listBookingChannelPrivacyPolicy({
        bookingChannelId,
      });
    } catch (error) {
      context.dispatch('globalMessages/addError', { message: 'bookingChannelState.getPrivacyPolicyListError' }, { root: true });
      return false;
    }
  },
  async createBookingChannelPrivacyPolicy(context, { bookingChannelId, payload }) {
    try {
      const bookingChannelConnector = context.getters.bookingChannelConnector(context);
      return await bookingChannelConnector.createBookingChannelPrivacyPolicy({
        bookingChannelId,
        payload,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });
    } catch (error) {
      context.dispatch('globalMessages/addError', { message: 'bookingChannelState.createPrivacyPolicyError' }, { root: true });
      return false;
    }
  },
  async deleteBookingChannelPrivacyPolicy(context, { bookingChannelId, privacyPolicyId }) {
    try {
      const bookingChannelConnector = context.getters.bookingChannelConnector(context);

      await bookingChannelConnector.deleteBookingChannelPrivacyPolicy({
        bookingChannelId,
        privacyPolicyId,
      });

      return true;
    } catch (error) {
      context.dispatch('globalMessages/addError', { message: 'bookingChannelState.deletePrivacyPolicyError' }, { root: true });
      return false;
    }
  },

  // ======== Terms and Conditions ========

  async getBookingChannelTermAndConditionList(context, { bookingChannelId }) {
    try {
      const bookingChannelConnector = context.getters.bookingChannelConnector(context);
      return await bookingChannelConnector.listBookingChannelTermAndCondition({
        bookingChannelId,
      });
    } catch (error) {
      context.dispatch('globalMessages/addError', { message: 'bookingChannelState.getTermAndConditionListErrors' }, { root: true });
      return false;
    }
  },
  async createBookingChannelTermAndCondition(context, { bookingChannelId, payload }) {
    try {
      const bookingChannelConnector = context.getters.bookingChannelConnector(context);
      return await bookingChannelConnector.createBookingChannelTermAndCondition({
        bookingChannelId,
        payload,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });
    } catch (error) {
      context.dispatch('globalMessages/addError', { message: 'bookingChannelState.createTermAndConditionError' }, { root: true });
      return false;
    }
  },
  async deleteBookingChannelTermAndCondition(context, { bookingChannelId, termAndConditionId }) {
    try {
      const bookingChannelConnector = context.getters.bookingChannelConnector(context);

      await bookingChannelConnector.deleteBookingChannelTermAndCondition({
        bookingChannelId,
        termAndConditionId,
      });

      return true;
    } catch (error) {
      context.dispatch('globalMessages/addError', { message: 'bookingChannelState.deleteTermAndConditionError' }, { root: true });
      return false;
    }
  },

  // ======== App Publish Keys ========
  async getBookingChannelAppPublishKeySettings(context, { bookingChannelId }) {
    try {
      const bookingChannelConnector = context.getters.bookingChannelConnector(context);
      return await bookingChannelConnector.getBookingChannelAppPublishKeys({
        bookingChannelId,
      });
    } catch (error) {
      context.dispatch('globalMessages/addError', { message: 'bookingChannelState.getBookingChannelAppPublishKeySettings' }, { root: true });
      return false;
    }
  },
  async setBookingChannelAppPublishKeys(context, { bookingChannelId, payload }) {
    try {
      const bookingChannelConnector = context.getters.bookingChannelConnector(context);
      return await bookingChannelConnector.setBookingChannelAppPublishKeys({
        bookingChannelId,
        payload,
      });
    } catch (error) {
      context.dispatch('globalMessages/addError', { message: 'bookingChannelState.setBookingChannelAppPublishKeySettings' }, { root: true });
      return false;
    }
  },
};

const mutations = {
  setBookingChannelConfig(state, {
    authCallbackUrl, colorScheme, name, id, logo, oidcClientId, postLogoutUrl, currencySymbol, fleets,
  }) {
    state.authCallbackUrl = authCallbackUrl;
    state.colorScheme = colorScheme;
    state.companyName = name;
    state.id = id;
    state.logoURL = logo;
    state.oidcClientId = oidcClientId;
    state.postLogoutUrl = postLogoutUrl;
    state.currencySymbol = currencySymbol;
    state.fleets = fleets;
  },
};

export default {
  namespaced: true,
  state: defaultState,
  getters,
  actions,
  mutations,
};
